import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LanguageContext } from "../App";
import { IMAGES, BOOKING_URL } from "../translations";
import MapAurora from "../components/MapAurora";

export default function LocationPage() {
  const { lang, t } = useContext(LanguageContext);
  const loc = t.location;

  return (
    <>
      {/* ─── 1. HERO FULLSCREEN ─────────────────────────────────── */}
      <div
        data-testid="location-hero"
        className="relative h-screen min-h-[600px] flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-[#0d1a22] location-hero-bg"
          style={{
            backgroundImage: `url(${IMAGES.location_hero})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1916]/80 via-[#1A1916]/30 to-transparent" />
        {/* Shimmer animato sull'acqua */}
        <div className="sea-shimmer" />

        <div className="relative z-10 w-full px-6 md:px-16 pb-16 md:pb-24 max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-4"
          >
            {loc.overline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-['Cormorant_Garamond',serif] text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light text-[#FDFBF7] tracking-tight leading-tight mb-4"
          >
            {loc.page_headline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="font-['Manrope',sans-serif] text-base md:text-lg text-[#FDFBF7]/65 max-w-xl leading-relaxed"
          >
            {loc.page_subtitle}
          </motion.p>
        </div>
      </div>

      {/* ─── 2. OUTDOOR — 2 columns ─────────────────────────────────── */}
      <section
        data-testid="location-outdoor"
        className="bg-[#EBE5DD] py-24 md:py-32 px-6"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: staggered images */}
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9 }}
              className="overflow-hidden aspect-[4/3] md:aspect-[3/4]"
            >
              <img
                src={IMAGES.outdoor[0]}
                alt="Delfino Verde Navigazione"
                loading="lazy"
                className="w-full h-full object-cover boat-float"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="overflow-hidden aspect-[4/3] md:aspect-[3/4] md:mt-16"
            >
              <img
                src={IMAGES.outdoor[1]}
                alt="Torri di Slivia"
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.div>
          </div>

          {/* Right: text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1 }}
            className="flex flex-col gap-8"
          >
            <div>
              <p className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-5">
                {loc.overline}
              </p>
              <h2 className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl md:text-5xl font-light text-[#2A2726] tracking-tight leading-tight mb-6">
                {loc.outdoor_headline}
              </h2>
              <p className="font-['Manrope',sans-serif] text-base text-[#5C5754] leading-relaxed mb-8">
                {loc.outdoor_text}
              </p>
            </div>

            {/* Activities */}
            <div className="flex flex-col gap-3">
              {loc.outdoor_activities.map((activity, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex items-center gap-4 py-3 border-b border-[#DDD8CE] last:border-0"
                >
                  <span className="font-['Cormorant_Garamond',serif] text-2xl font-light text-[#DDD8CE]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-['Manrope',sans-serif] text-sm uppercase tracking-[0.2em] text-[#2A2726]">
                    {activity}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Book CTA */}
            <Link
              to="/prenota"
              data-testid="location-outdoor-book"
              className="inline-flex items-center gap-2 font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#7B907F] hover:gap-3 transition-all duration-300 border-b border-[#7B907F] pb-1 self-start"
            >
              {loc.book_cta} <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ─── 3. THE COAST — fullscreen cinematic ───────────────────── */}
      <div
        data-testid="location-coast"
        className="relative h-[65vh] md:h-[75vh] overflow-hidden flex items-end"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.castle})`, backgroundPosition: "center 40%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1916]/70 via-transparent to-transparent" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative z-10 w-full px-6 md:px-16 pb-14 md:pb-20 max-w-7xl mx-auto"
        >
          <h2 className="font-['Cormorant_Garamond',serif] text-4xl md:text-5xl lg:text-6xl font-light text-[#FDFBF7] tracking-tight mb-3">
            {loc.coast_headline}
          </h2>
          <p className="font-['Manrope',sans-serif] text-sm md:text-base text-[#FDFBF7]/65 whitespace-pre-line leading-relaxed max-w-md">
            {loc.coast_text}
          </p>
        </motion.div>
      </div>

      {/* ─── 4. INFO + MAP ───────────────────────────────────────────── */}
      <section className="bg-[#FDFBF7] py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Key distances */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#DDD8CE] mb-14">
            {[
              { num: "100m", label: loc.tag1 },
              { num: "150m", label: loc.tag2 },
              { num: "10'",  label: loc.tag3 },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-[#FDFBF7] px-8 py-10 text-center"
              >
                <p className="font-['Cormorant_Garamond',serif] text-5xl font-light text-[#7B907F] mb-2">
                  {item.num}
                </p>
                <p className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#8A847F]">
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Mappa interattiva — stile identico al sito originale */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1 }}
            data-testid="location-map-wrapper"
          >
            <MapAurora height="65vh" />
          </motion.div>
        </div>
      </section>

      {/* ─── 5. FINAL CTA ────────────────────────────────────────────── */}
      <section className="bg-[#2A2726] py-16 md:py-20 px-6 text-center" data-testid="loc-cta-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto"
        >
          <a
            href="https://www.google.com/maps/place/Frazione+Duino+15,+34011+Duino+Aurisina,+TS/@45.7730386,13.6067454,17z"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="loc-maps-btn"
            className="inline-flex items-center justify-center border border-[#FDFBF7]/30 text-[#FDFBF7] font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] w-full sm:w-auto px-10 py-4 hover:border-[#FDFBF7]/80 transition-all duration-300"
          >
            {loc.cta}
          </a>
          <Link
            to="/prenota"
            data-testid="loc-book-btn"
            className="inline-flex items-center justify-center bg-[#7B907F] text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] w-full sm:w-auto px-10 py-4 hover:bg-[#687A6C] transition-all duration-300"
          >
            {loc.book_cta}
          </Link>
        </motion.div>
      </section>
    </>
  );
}
